import { animate, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TealiumAnalyticsEvent, TealiumAnalyticsService } from '@ddt-web/analytics';
import { HoldingsComponentDCR } from '@ddt-web/config';
import { InjectConfig, PcConfigKey } from '@ddt-web/config-layer';
import { BaseHolding, PortfolioConstants, PortfolioReference, PortfolioStructureInfo, TreeNodeMap, WebUserDataConstants } from '@ddt-web/domain';
import * as get from 'lodash/get';

import { HoldingTableDetails } from '../../models/holding-table-details.interface';
import { BlendPortfolioUtil } from '../../utils/blend-portfolio.util';

@Component({
	selector: 'ac-blending-accordion-panel',
	templateUrl: './blending-accordion-panel.component.html',
	styleUrls: ['./blending-accordion-panel.component.scss'],
	animations: [
		trigger('slideInOut', [
			transition(':enter', [style({ transform: 'translateY(-100%)', opacity: 0 }), animate('400ms ease-in-out', style({ transform: 'translateY(0%)', opacity: 1 }))]),
			transition(':leave', [animate('400ms ease-in-out', style({ transform: 'translateY(-100%)', opacity: 0 }))])
		])
	]
})
export class BlendingAccordionPanelComponent implements OnChanges, OnInit {
	@Input() public inDialog: boolean;
	@Input() public panelDepth: number;
	@Input() public isWeightActive: boolean;
	@Input() public portfolioChild: PortfolioReference;
	@Input() public allCircularPortfolios?: PortfolioStructureInfo;
	@Input() public holdingDetailsChild: HoldingTableDetails;
	@Input() public isCollapsed: boolean = true;
	@Input() public isEditable: boolean = true;
	@Input() public portChildrenMap: TreeNodeMap;
	@Input() public parentProjectedTotalAmount: number;
	@Output() public editedData: EventEmitter<any> = new EventEmitter<any>();
	@Output() public onToggleChange: EventEmitter<any> = new EventEmitter<any>();
	@Output() public removeChildClicked: EventEmitter<PortfolioReference> = new EventEmitter<PortfolioReference>();
	@Output() public openChildViewClicked: EventEmitter<{ child: PortfolioReference; projectedAmount: number }> = new EventEmitter<{
		child: PortfolioReference;
		projectedAmount: number;
	}>();

	public BlendPortfolioUtil = BlendPortfolioUtil;

	public childrenAmountBreakdown: { [identifier: string]: number } = {};
	public isCircularPortfolio = false;
	public isChildInvalid: boolean = false;
	public readonly PORTFOLIO: string = WebUserDataConstants.TYPE.PORTFOLIO;
	public readonly PORTFOLIO_POSITION: string = WebUserDataConstants.TYPE.PORTFOLIO_POSITION;
	public readonly MODEL_PORTFOLIO: string = WebUserDataConstants.TYPE.MODEL_PORTFOLIO;
	public readonly MODEL_PORTFOLIO_TEXT: string = HoldingsComponentDCR.PORTFOLIO_TYPE_OPTIONS.MODEL_PORTFOLIO;

	@InjectConfig(PcConfigKey.ENABLE_BLENDING) public readonly enableBlending: boolean = false;

	constructor(private tealiumAnalyticsService: TealiumAnalyticsService) { }

	public ngOnInit(): void {
		const posType = get(this.portfolioChild, 'positionType');
		this.isChildInvalid = posType === PortfolioConstants.POSITION_TYPE.INVALID;
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes.portChildrenMap) {
			this.portChildrenMap = changes.portChildrenMap.currentValue;
		}

		if (changes.portfolioChild) {
			this.portfolioChild = changes.portfolioChild.currentValue;
			this.portChildrenMap[this.portfolioChild.identifier].childrenRef.forEach(subChild => {
				this.childrenAmountBreakdown = {
					...this.childrenAmountBreakdown,
					[subChild.identifier]: this.getAmountBreakdown(subChild.weight)
				};
			});
			if (this.allCircularPortfolios && this.allCircularPortfolios.parentId && this.allCircularPortfolios.portfolioId) {
				this.disableAccordion(this.allCircularPortfolios.portfolioId, this.allCircularPortfolios.parentId);
			} else {
				this.isCircularPortfolio = false;
			}
		}
	}

	public disableAccordion(errorPortfolioId: string, errorParentId: string): void {
		const portParentsList = this.portChildrenMap[this.portfolioChild.identifier].parents;

		const currentPortHasError = errorPortfolioId === this.portfolioChild.identifier; // Checks the current portfolio is the one which has the error
		const directParentHasError = !portParentsList.length && this.portfolioChild.identifier === errorParentId; // Checks that, if the portfolio is a loop with itself as a direct parent
		const parentHasErrors = portParentsList.includes(errorParentId); // Checks that, the current portfolio's parent is the same as the error parent

		this.isCircularPortfolio = currentPortHasError && (directParentHasError || parentHasErrors);
	}

	public togglePanel(event: Event): void {
		if (!this.isCircularPortfolio) {
			event.preventDefault();
			const target = event.target as HTMLElement;
			const attribute = target.parentElement && (target.parentElement.getAttribute('type') ||
				(target.parentElement.parentElement && target.parentElement.parentElement.getAttribute('type')));
			if (attribute !== 'info-circle') {
				this.onToggleChange.emit();
			}
		}
		const tealiumEventName = 'editor-left-section:' + (this.isCollapsed ? 'expand:' : 'collapse:') + 'level ' + this.panelDepth + ':' + this.portfolioChild.identifier;
		const tealiumEvent = new TealiumAnalyticsEvent(tealiumEventName, {});
		this.tealiumAnalyticsService.trackEventTealium(tealiumEvent);
	}

	public emitOpenChildView(child: PortfolioReference): void {
		this.openChildViewClicked.emit({ child: child, projectedAmount: this.childrenAmountBreakdown[child.identifier] || this.parentProjectedTotalAmount });
	}

	/**
	 * Called when emitter is emitting from child accordion
	 */
	public emitOpenChildViewFromChildAccordion(event: { child: PortfolioReference; projectedAmount: number }): void {
		this.openChildViewClicked.emit({ child: event.child, projectedAmount: event.projectedAmount });
	}

	public removeChild(child: PortfolioReference): void {
		this.removeChildClicked.emit(child);
	}

	public editedChild(child: PortfolioReference, newValue: number): void {
		this.editedData.emit({ child: child, newValue: newValue });
	}

	public getAmountBreakdown(childWeight: number): number {
		if (!this.parentProjectedTotalAmount) {
			return 0;
		} else {
			return this.parentProjectedTotalAmount / 100 * childWeight;
		}
	}

	public trackByIdentifier(child: BaseHolding): string {
		return child.identifier;
	}

}
